/**
 * The refusal auth-service answers when a member who owns missions is made a
 * Guest (PATCH .../members/{user_id}/access-audience, 409, agion/Agion#3043;
 * owner rule 2026-10-12). The server decides which missions the member owns,
 * who may take each over and whether the administrator may see a mission's
 * name; this module only reads its body, which the HTTP adapter attaches raw
 * (wire names) as `error.data`. Ids are for the request, never shown.
 */

export const OWNERSHIP_HANDOVER_CODES = ['ownership_handover_required', 'no_eligible_successor', 'ownership_handover_invalid', 'last_secret_keeper'] as const
export type OwnershipHandoverCode = (typeof OWNERSHIP_HANDOVER_CODES)[number]

/** One person who may take over a mission. */
export interface MissionSuccessor {
  userId: string
  displayName: string
}

/** One mission the member owns, as the administrator may see it. */
export interface OwnedMission {
  missionId: string
  /** Absent when the administrator cannot view the mission. */
  name: string | undefined
  /** The served level; null for a mission not classified yet. */
  accessClassification: string | null
  eligibleSuccessors: MissionSuccessor[]
  /**
   * How many eligible people the server left out of a list capped at 50;
   * absent when the list is complete. The rest are found by name through
   * handover-successors.
   */
  moreSuccessors?: number
}

/** The answer of GET .../members/{user_id}/handover-successors (camelCased by the HTTP adapter). */
export interface SuccessorSearch {
  successors: MissionSuccessor[]
  /** How many more people match than the answer lists. */
  moreSuccessors?: number
}

export interface OwnershipHandoverRefusal {
  /**
   * ownership_handover_required: no handover was sent; every owned mission.
   * no_eligible_successor: only the missions nobody else can take over.
   * ownership_handover_invalid: the missions or successors changed; every
   * owned mission with fresh successors.
   * last_secret_keeper: the member is the last Owner or Admin inside the
   * listed Secret missions (owned or not); another must be added there first.
   */
  code: OwnershipHandoverCode
  missions: OwnedMission[]
}

/** The administrator's pick for one owned mission. */
export interface MissionOwnershipHandover {
  missionId: string
  newOwnerUserId: string
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

const nonEmpty = (value: unknown): value is string => typeof value === 'string' && value !== ''

function readSuccessor(value: unknown): MissionSuccessor | null {
  const item = asRecord(value)
  if (!item || !nonEmpty(item['user_id']) || !nonEmpty(item['display_name'])) return null
  return { userId: item['user_id'], displayName: item['display_name'] }
}

// The count of people left out; anything else means the list is complete.
function readMore(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : undefined
}

function readMission(value: unknown): OwnedMission | null {
  const item = asRecord(value)
  if (!item || !nonEmpty(item['mission_id'])) return null
  const successors = Array.isArray(item['eligible_successors']) ? item['eligible_successors'] : []
  const mission: OwnedMission = {
    missionId: item['mission_id'],
    name: nonEmpty(item['name']) ? item['name'] : undefined,
    accessClassification: nonEmpty(item['access_classification']) ? item['access_classification'] : null,
    eligibleSuccessors: successors.map(readSuccessor).filter((s): s is MissionSuccessor => s !== null),
  }
  const more = readMore(item['more_successors'])
  if (more !== undefined) mission.moreSuccessors = more
  return mission
}

/** The handover refusal carried by a failed change, or undefined for any other failure. */
export function readOwnershipHandoverRefusal(error: unknown): OwnershipHandoverRefusal | undefined {
  const record = asRecord(error)
  if (!record || record['status'] !== 409) return undefined
  const data = asRecord(record['data'])
  const code = data?.['code']
  if (!data || !OWNERSHIP_HANDOVER_CODES.includes(code as OwnershipHandoverCode)) return undefined
  const raw = Array.isArray(data['missions']) ? data['missions'] : []
  const missions = raw.map(readMission)
  // A mission the page cannot name by id cannot be handed over: treat the body as unreadable.
  if (missions.some((m) => m === null)) return undefined
  return { code: code as OwnershipHandoverCode, missions: missions as OwnedMission[] }
}

/** The successor search answer; a person without an id or a name is dropped, never shown by id. */
export function readSuccessorSearch(value: unknown): SuccessorSearch {
  const body = asRecord(value)
  const raw = Array.isArray(body?.['successors']) ? (body!['successors'] as unknown[]) : []
  const successors = raw.flatMap((item) => {
    const s = asRecord(item)
    return s && nonEmpty(s['userId']) && nonEmpty(s['displayName']) ? [{ userId: s['userId'], displayName: s['displayName'] }] : []
  })
  const more = readMore(body?.['moreSuccessors'])
  return more === undefined ? { successors } : { successors, moreSuccessors: more }
}
